import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import ProductSelector, { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    subheading = "subheading",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.subheading]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    return {
        canOrderUpload: settingsCollection.orderSettings.canOrderUpload,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        orderUploadPageNavLink: getPageLinkByPageType(state, "OrderUploadPage"),
        quickOrderPageNavLink: getPageLinkByPageType(state, "QuickOrderPage"),
        allowQuickOrder: settingsCollection.orderSettings.allowQuickOrder,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable(addToCart),
};

export type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface QuickOrderStyles {
    wrapper?: InjectableCss;
    restrictedWidthSectionWrapper?: InjectableCss;
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    subHeadingText?: TypographyPresentationProps;
    linksGridItem?: GridItemProps;
    orderUploadLink?: LinkPresentationProps;
    orderMultipleItemsLink?: LinkPresentationProps;
    productSelector?: ProductSelectorStyles;
    banner?: InjectableCss;
}

export const quickOrderStyles: QuickOrderStyles = {
    wrapper: {
        // AMR-343
        css: css`
            text-align: center;
            display: block;
            margin: 0 -15px 15px -15px;
            background-color: ${getColor("secondary.main")};
            overflow: visible;
        `,
    },
    restrictedWidthSectionWrapper: {
        css: css`
            margin: auto;
            padding-top: 20px;
            padding-bottom: 40px;
            width: 40%;
            ${({ theme }: { theme: BaseTheme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(
                    theme,
                    maxWidths.map(mw =>
                        mw
                            ? css`
                                  max-width: ${mw}px;
                              `
                            : null,
                    ),
                );
            }}
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: 75%;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    container: {
        gap: 15,
        css: css`
            margin: auto;
        `,
    },
    titleGridItem: { width: [12, 12, 12, 12, 12] },
    titleText: {
        variant: "h1",
        css: css`
            margin: auto;
            color: white;
        `,
    },
    subHeadingText: {
        variant: "h5",
        css: css`
            margin: auto;
            color: white;
            font-weight: 300;
            padding-top: 15px;
            padding-bottom: 20px;
        `,
    },
    linksGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-bottom: 20px;
                        padding-top: 0;
                    `,
                    css`
                        justify-content: flex-end;
                        padding-top: 20px;
                    `,
                    css`
                        justify-content: flex-end;
                        padding-top: 20px;
                    `,
                    css`
                        justify-content: flex-end;
                        padding-top: 20px;
                    `,
                    css`
                        justify-content: flex-end;
                        padding-top: 20px;
                    `,
                ])}
        `,
    },
    orderMultipleItemsLink: {
        css: css`
            margin-left: 20px;
        `,
    },
    productSelector: {
        container: {
            gap: 10,
        },
        searchGridItem: {
            width: [12, 12, 12, 7, 7],
        },
        qtyGridItem: {
            width: [3, 3, 6, 2, 2],
        },
        buttonGridItem: {
            width: [12, 12, 6, 3, 3],
        },
        selectButton: {
            typographyProps: {
                size: 18,
            },
            css: css`
                width: 100%;
                align-self: flex-end;
                border-radius: 6px;
            `,
        },
    },
    banner: {
        css: css`
            background-color: ${getColor("primary.main")};
            height: 10px;
        `,
    },
};

export const QuickOrder = ({
    fields,
    canOrderUpload,
    showAddToCartConfirmationDialog,
    orderUploadPageNavLink,
    quickOrderPageNavLink,
    toaster,
    addToCart,
    allowQuickOrder,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [errorMessage, setErrorMessage] = useState<React.ReactNode>("");

    if (!allowQuickOrder) {
        return null;
    }
    const styles = quickOrderStyles;

    const addProductToCart = async (productInfo: ProductInfo, product: ProductModel) => {
        if (
            !product.quoteRequired &&
            !product.allowZeroPricing &&
            productInfo.pricing &&
            getUnitNetPrice(productInfo.pricing, 1).price === 0
        ) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const cartline = (await addToCart({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
            onError: () => {
                setErrorMessage(siteMessage("Product_NotFound"));
            },
        })) as CartLineModel;

        if (showAddToCartConfirmationDialog) {
            toaster.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
            });
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.banner} />
            <StyledWrapper {...styles.restrictedWidthSectionWrapper}>
                <GridContainer {...styles.container} data-test-selector="quickOrderWidget">
                    <GridItem {...styles.titleGridItem}>
                        <Typography {...styles.titleText} data-test-selector="quickOrderWidget_title">
                            {translate("Quick Order")}
                        </Typography>
                    </GridItem>

                    {canOrderUpload && (
                        <GridItem {...styles.linksGridItem}>
                            <Link
                                {...styles.orderUploadLink}
                                href={orderUploadPageNavLink ? orderUploadPageNavLink.url : undefined}
                                data-test-selector="quickOrderWidget_uploadAnOrder"
                            >
                                {translate("Upload an Order")}
                            </Link>
                            <Link
                                {...styles.orderMultipleItemsLink}
                                href={quickOrderPageNavLink ? quickOrderPageNavLink.url : undefined}
                                data-test-selector="quickOrderWidget_orderMultipleItems"
                            >
                                {translate("Order Multiple Items")}
                            </Link>
                        </GridItem>
                    )}
                </GridContainer>
                {fields.subheading && (
                    <Typography {...styles.subHeadingText}>{parse(fields.subheading, parserOptions)}</Typography>
                )}
                <ProductSelector
                    selectButtonTitle={translate("Add to Cart")}
                    onSelectProduct={addProductToCart}
                    productIsConfigurableMessage={siteMessage("QuickOrder_CannotOrderConfigurable")}
                    productIsUnavailableMessage={siteMessage("QuickOrder_ProductIsUnavailable")}
                    customErrorMessage={errorMessage}
                    extendedStyles={styles.productSelector}
                    data-test-selector="quickOrderWidget_addToCart"
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(QuickOrder)),
    definition: {
        group: "Basic",
        icon: "cart-shopping-fast",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
